import React, { useState } from 'react';
import { FormTheme } from '../../types';
import { t } from '../../utils/i18n';

interface FileUploadProps {
  id: string;
  label: string;
  required?: boolean;
  value: File | null;
  onChange: (value: File | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  theme: FormTheme;
  config: Record<string, any>;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  id, label, required, value, onChange, onFocus, onBlur,
  error, disabled, config,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const maxSize = config.maxSize || 5242880; // 5 MB
  const allowedTypes = config.allowedTypes || [];

  const handleFile = (file: File) => {
    if (file.size > maxSize) {
      setValidationError(t('file.tooLarge', { name: file.name, max: (maxSize / 1024 / 1024).toFixed(2) }));
      return;
    }
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      setValidationError(t('file.invalidType', { name: file.name, types: allowedTypes.join(', ') }));
      return;
    }
    setValidationError(null);
    onChange(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFile(e.target.files[0]);
  };

  return (
    <div className="hexaforms-field">
      <label htmlFor={id} className="hexaforms-label">
        {label}
        {required && <span className="hexaforms-required">*</span>}
      </label>
      <div
        className={`hexaforms-file-upload${dragActive ? ' hexaforms-file-upload-active' : ''}${error ? ' hexaforms-file-upload-error' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id={id}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          accept={allowedTypes.join(',')}
          className="hexaforms-file-input"
        />
        <div className="hexaforms-file-upload-content">
          {value ? (
            <div className="hexaforms-file-upload-selected">
              <span>✓ {value.name}</span>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="hexaforms-file-upload-remove"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="hexaforms-file-upload-placeholder">
              <span>{t('upload.placeholder')}</span>
            </div>
          )}
        </div>
      </div>
      {validationError && <div className="hexaforms-error">{validationError}</div>}
      {error && <div className="hexaforms-error">{error}</div>}
    </div>
  );
};
